import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/base/badges/badges";
import { cx } from "@/utils/cx";
import type { PaymentIntentStatus } from "../../schemas/payment";

/**
 * UC-004 PollingStatusBadge — payment status indicator s 30s refresh countdown
 * (Phase 19, REQ-API-003 + REQ-BIZ-017 + REQ-UI-018 color-not-only).
 *
 * Reaguje na `status` z `usePaymentIntent` query (refetchInterval = 30000ms).
 * Per princip #4 sync transparency — user vždy ví, jak čerstvá data jsou.
 *
 * Color-not-only:
 *   - status="open"     → ● grey + "Otevřená" + countdown "(za N s)"
 *   - status="paid"     → ✓ green + "Zaplaceno"
 *   - status="expired"  → ⚠ amber + "Vypršela platnost"
 *
 * Countdown reset se synchronizuje s `lastFetchedAt` prop (z React Query
 * `dataUpdatedAt`).
 */

export interface PollingStatusBadgeProps {
  status: PaymentIntentStatus;
  /** Epoch ms z `useQuery().dataUpdatedAt`. */
  lastFetchedAt: number;
  /** Interval mezi refetch — default 30 000 ms. */
  refetchMs?: number;
}

const SYMBOL: Record<PaymentIntentStatus, string> = {
  open: "●",
  paid: "✓",
  expired: "⚠",
};

const BADGE_COLOR: Record<PaymentIntentStatus, "blue" | "success" | "warning"> = {
  open: "blue",
  paid: "success",
  expired: "warning",
};

export function PollingStatusBadge({
  status,
  lastFetchedAt,
  refetchMs = 30_000,
}: PollingStatusBadgeProps) {
  const { t } = useTranslation("tenant");
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    // Countdown ticker — 1s interval. Only ticks for "open" status.
    if (status !== "open") return undefined;
    const handle = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(handle);
  }, [status]);

  const remainingMs = Math.max(0, refetchMs - (now - lastFetchedAt));
  const remainingSec = Math.ceil(remainingMs / 1000);

  const label =
    status === "paid"
      ? t("payment.status.paid")
      : status === "expired"
        ? t("payment.status.expired")
        : t("payment.status.open");

  const countdown =
    status === "open"
      ? ` (${t("payment.status.refreshIn", { seconds: remainingSec })})`
      : "";

  const toneClass =
    status === "paid"
      ? "font-semibold text-success-primary"
      : status === "expired"
        ? "font-semibold text-warning-primary"
        : "text-tertiary";

  return (
    <span
      className={cx("inline-flex items-center gap-1.5 text-sm", toneClass)}
      data-testid="polling-status-badge"
      data-status={status}
      aria-live="polite"
    >
      <span className="inline-block h-2 w-2 rounded-full bg-current" aria-hidden="true">
        {SYMBOL[status]}
      </span>
      <Badge color={BADGE_COLOR[status]}>{label}</Badge>
      {status === "open" && (
        <span data-testid="polling-status-countdown">
          {countdown}
        </span>
      )}
    </span>
  );
}
